<script setup lang="ts">
import { computed } from 'vue'

import type { AlertSummarySection } from '../composables/useAlertSummary'
import { AlertMetricLabel, useAlertMetricCatalog } from '../models/reports-bridge'
import type { AlertStepId } from '../service/alertValidator'

import ConditionBadge from './ConditionBadge.vue'

import { useI18n } from '@/shared/i18n'
import { Badge } from '@/shared/ui'

const props = defineProps<{
  sections: AlertSummarySection[]
  activeStep: AlertStepId
  editable?: boolean
  editableSteps?: readonly AlertStepId[]
}>()
const emit = defineEmits<{ edit: [step: AlertStepId] }>()
const { t } = useI18n()
const catalog = useAlertMetricCatalog()

/**
 * Legacy parity (old's summary-preview visibility rule): a section appears once it has something
 * to show, or while the step that owns it is the active one (so it can visibly fill in as you
 * type) — never as a permanent "Not set" row for a step you've already moved past.
 */
const visibleSections = computed(() =>
  props.sections.filter((section) => {
    const hasValue = section.kind === 'text' ? Boolean(section.value) : section.values.length > 0
    return hasValue || section.step === props.activeStep
  }),
)

/** Mirrors `FilterChip.vue`'s `selectionSummary`: the inline chip already collapses 3+ values
 * to "N selected" — this sidebar chip disagreed, showing the raw joined list instead. */
function filterValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.length > 2
      ? t('alerts.summary.selectedCount', { count: value.length })
      : value.map(String).join(' – ')
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return ''
}
</script>

<template>
  <section
    class="alert-summary grid gap-md"
    :class="{ 'alert-summary--editable': editable }"
    :aria-label="t('alerts.summary.title')"
  >
    <!-- Legacy parity: old's step-5 review card has no heading at all — only the sidebar does. -->
    <h3 v-if="!editable">{{ t('alerts.summary.title') }}</h3>
    <dl>
      <div
        v-for="section in visibleSections"
        :key="section.key"
        class="alert-summary__row grid grid-cols-[minmax(0,_1fr)_auto] gap-2xs"
      >
        <dt>{{ section.label }}</dt>
        <dd>
          <span v-if="section.kind === 'text'" class="alert-summary__value wrap-break-word">{{
            section.value || '-'
          }}</span>
          <div
            v-else-if="section.kind === 'conditions'"
            class="alert-summary__chips flex flex-wrap gap-2xs"
          >
            <span v-if="section.values.length > 1" class="text-muted text-xs">
              {{ t(`alerts.condition.operator.${section.operator ?? 'or'}`) }}
            </span>
            <Badge
              v-for="condition in section.values"
              :key="condition.column.field"
              tone="info"
              shape="rounded"
            >
              <ConditionBadge :condition="condition" />
            </Badge>
          </div>
          <div
            v-else-if="section.kind === 'filters'"
            class="alert-summary__chips flex flex-wrap gap-2xs"
          >
            <Badge v-for="filter in section.values" :key="filter.field" tone="info" shape="rounded">
              <span
                class="alert-summary__filter-chip-text inline-block max-w-[12rem] overflow-hidden align-bottom whitespace-nowrap text-ellipsis"
                :title="`${catalog.metricLabel(filter.field)} ${filterValue(filter.value)}`"
              >
                <AlertMetricLabel
                  :field="filter.field"
                  :column="catalog.getColumn(filter.field) ?? undefined"
                />
                {{ filterValue(filter.value) }}
              </span>
            </Badge>
          </div>
          <div v-else class="alert-summary__chips flex flex-wrap gap-2xs">
            <Badge v-for="value in section.values" :key="value" tone="info" shape="rounded">{{
              value
            }}</Badge>
          </div>
        </dd>
        <button
          v-if="editable && (!editableSteps || editableSteps.includes(section.step))"
          type="button"
          class="alert-summary__edit shrink-0 border-0 bg-transparent p-0 text-info text-[0.875rem] font-light cursor-pointer [&:hover]:font-medium [&:hover]:underline"
          @click="emit('edit', section.step)"
        >
          {{ t('alerts.actions.edit') }}
        </button>
      </div>
    </dl>
  </section>
</template>

<style scoped>
.alert-summary dl {
  display: grid;
  gap: var(--ui-space-md);
  margin: 0;
}
.alert-summary h3 {
  margin: 0;
}
.alert-summary dd {
  margin: 0;
}
.alert-summary.alert-summary--editable {
  border: 1px solid var(--ui-color-border);
  border-radius: var(--ui-radius-lg);
  background-color: var(--ui-color-surface);
  overflow: hidden;
}
.alert-summary.alert-summary--editable dl {
  gap: 0;
}
.alert-summary.alert-summary--editable .alert-summary__row {
  padding-block: 0.875rem;
  padding-inline: 1.125rem;
}
.alert-summary.alert-summary--editable .alert-summary__row:not(:first-child) {
  border-top: 1px solid var(--ui-color-border);
}
.alert-summary .alert-summary__row dt {
  grid-column: 1 / -1;
  color: var(--ui-color-muted);
  font-size: 0.75rem;
}
.alert-summary .alert-summary__row dd {
  color: var(--ui-color-fg);
  font-size: 0.875rem;
}
.alert-summary.alert-summary--editable .alert-summary__row dd {
  font-size: 0.75rem;
}
.alert-summary.alert-summary--editable .alert-summary__value {
  font-weight: 600;
}
</style>
